import React, { useState } from "react";
import { Container, Grid, Typography, Box } from "@mui/material";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import StarIcon from "@mui/icons-material/Star";
import { Section, SectionLabel, DP, GradientText } from "./HomeStyles";
import { useReveal } from "../../hooks/useReveal";
import { keyframes } from "@mui/material";
import { useLanguage } from "../../contexts/LanguageContext";
import { useNavigate } from "react-router-dom";

const cardReveal = keyframes`
  from { opacity: 0; transform: translateY(40px) scale(0.95); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const testimonialData = [
  { name: "Mikaella Samson",  roleKey: "owner"    as const, rating: 5, text: "Today was my first visit with my 4-year-old Labrador, and I was very pleased with our experience with Dr. Mira. She took her time, asked all the right questions, and made sure she fully understood my dog's history and behavior. I felt heard, understood, and guided.", pet: "🐕", color: "#E3F2FD" },
  { name: "Zeina Ferreira",   roleKey: "regular"  as const, rating: 5, text: "Dr. Paw is, without a doubt, the best clinic I've ever taken my pets to. Dr. Mira is exceptional—compassionate, attentive, and genuinely devoted to every animal she meets. Her team is just as wonderful: committed, patient, and always ready to give the best care.", pet: "🐶", color: "#F3E5F5" },
  { name: "Ghinwa Douaihy",   roleKey: "owner"    as const, rating: 5, text: "Best doctor in the Middle East and North Africa 😍 and soon in the whole world!! Very ethical and professional, non materialistic, and above all she is the best mom and fur mother in town 💜", pet: "🐈", color: "#E8F5E9" },
  { name: "Aya Mourad",       roleKey: "longTime" as const, rating: 5, text: "I'm really grateful for the care my cat received at this clinic. They were very patient and understanding — especially since my cat was anxious. What I truly appreciated is that they focused on diagnosing properly before giving any medication, which made me feel very safe and confident.", pet: "🐾", color: "#FFF3E0" },
];

const Stars: React.FC<{ count: number }> = ({ count }) => (
  <Box sx={{ display: "flex", gap: 0.3, mb: 2, justifyContent: { xs: "center", md: "flex-start" } }}>
    {Array.from({ length: count }).map((_, i) => (
      <StarIcon key={i} sx={{ color: "#FFC107", fontSize: "1rem" }} />
    ))}
  </Box>
);

const TestimonialCard: React.FC<{ idx: number; visible: boolean }> = ({ idx, visible }) => {
  const [hovered, setHovered] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();
  const item = testimonialData[idx];
  const role = t.home.testimonials.roles[item.roleKey];

  const handleCardClick = () => {
    navigate(`/testimonial/${idx}`, { state: { testimonial: item, role } });
  };

  return (
    <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
      <Box onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
        onClick={handleCardClick}
        sx={{ backgroundColor: DP.white, borderRadius: "24px", p: { xs: 3, md: 3.5 }, border: `1px solid ${hovered ? DP.bluePale : DP.border}`, boxShadow: hovered ? `0 24px 64px ${DP.shadow}, 0 0 0 2px ${DP.bluePale}` : `0 4px 20px rgba(0,0,0,0.04)`, height: "100%", display: "flex", flexDirection: "column", opacity: visible ? 1 : 0, animation: visible ? `${cardReveal} 0.7s cubic-bezier(0.22,1,0.36,1) both` : "none", animationDelay: `${0.1 + idx * 0.12}s`, transition: "box-shadow 0.35s ease, border-color 0.35s ease, transform 0.35s cubic-bezier(0.34,1.56,0.64,1)", transform: hovered ? "translateY(-8px) scale(1.01)" : "translateY(0) scale(1)", position: "relative", overflow: "hidden", cursor: "pointer",
          "&::before": { content: '""', position: "absolute", top: 0, left: 0, right: 0, height: "100%", background: `linear-gradient(180deg, ${item.color}60 0%, transparent 40%)`, borderRadius: "24px", opacity: hovered ? 1 : 0, transition: "opacity 0.35s ease" },
        }}>
        <Box sx={{ width: 48, height: 48, borderRadius: "14px", backgroundColor: item.color, display: "flex", alignItems: "center", justifyContent: "center", mb: 2.5, mx: { xs: "auto", md: 0 }, transition: "transform 0.35s cubic-bezier(0.34,1.56,0.64,1)", transform: hovered ? "rotate(-8deg) scale(1.12)" : "rotate(0deg) scale(1)", position: "relative", zIndex: 1 }}>
          <FormatQuoteIcon sx={{ color: DP.blue, fontSize: "1.5rem" }} />
        </Box>
        <Stars count={item.rating} />
        <Typography sx={{ fontSize: "0.9rem", color: DP.textMid, lineHeight: 1.8, mb: 3, fontStyle: "italic", flexGrow: 1, position: "relative", zIndex: 1 }}>
          "{item.text}"
        </Typography>
        <Box onClick={(e) => { e.stopPropagation(); handleCardClick(); }} sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: "auto", pt: 2.5, borderTop: `1px solid ${DP.borderLight}`, position: "relative", zIndex: 1, cursor: "pointer", transition: "all 0.25s ease", "&:hover": { transform: "translateX(4px)" } }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "12px", backgroundColor: item.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem", transition: "transform 0.3s ease", transform: hovered ? "scale(1.1)" : "scale(1)" }}>
            {item.pet}
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: "0.88rem", color: DP.textDark }}>{item.name}</Typography>
            <Typography sx={{ fontSize: "0.72rem", color: DP.textMuted }}>{role}</Typography>
          </Box>
        </Box>
      </Box>
    </Grid>
  );
};

const Testimonials: React.FC = () => {
  const { ref, visible } = useReveal(0.1);
  const { t } = useLanguage();
  const tt = t.home.testimonials;

  return (
    <Section ref={ref} sx={{ backgroundColor: DP.white }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 }, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(28px)", transition: "all 0.8s cubic-bezier(0.22,1,0.36,1)" }}>
          <SectionLabel>{tt.sectionLabel}</SectionLabel>
          <Typography variant="h2" sx={{ fontSize: { xs: "2rem", md: "3rem" }, fontWeight: 800, color: DP.textDark, letterSpacing: "-0.02em", mt: 1, mb: 2 }}>
            {tt.heading}{" "}<GradientText>{tt.headingHighlight}</GradientText>
          </Typography>
          <Typography sx={{ fontSize: "1.05rem", color: DP.textMuted, maxWidth: 500, mx: "auto" }}>
            {tt.subtitle}
          </Typography>
        </Box>

        {/* Rating banner */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: { xs: 3, md: 5 }, flexWrap: "wrap", backgroundColor: DP.blueBg, borderRadius: "24px", px: { xs: 3, md: 6 }, py: 3.5, mb: 6, border: `1px solid ${DP.border}`, opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(20px)", transition: "all 0.8s cubic-bezier(0.22,1,0.36,1) 0.15s" }}>
          {[
            { num: "4.9", sub: tt.avgRating, extra: <Box sx={{ display: "flex", gap: 0.3, mt: 0.5 }}>{[1,2,3,4,5].map(i => <StarIcon key={i} sx={{ color: "#FFC107", fontSize: "1.1rem" }} />)}</Box> },
            { num: "1K+", sub: tt.reviews,   extra: null },
            { num: "98%", sub: tt.recommend, extra: null },
          ].map((item, i) => (
            <React.Fragment key={i}>
              {i > 0 && <Box sx={{ width: "1px", height: 48, backgroundColor: DP.border, display: { xs: "none", md: "block" } }} />}
              <Box sx={{ textAlign: "center" }}>
                <Typography sx={{ fontSize: { xs: "2rem", md: "2.6rem" }, fontWeight: 800, color: DP.textDark, lineHeight: 1, background: `linear-gradient(135deg, ${DP.navy}, ${DP.blue})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                  {item.num}
                </Typography>
                {item.extra}
                <Typography sx={{ fontSize: "0.75rem", color: DP.textMuted, fontWeight: 600, letterSpacing: "0.12em", mt: 0.5 }}>
                  {item.sub}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>

        <Grid container spacing={3}>
          {testimonialData.map((_, i) => (
            <TestimonialCard key={i} idx={i} visible={visible} />
          ))}
        </Grid>
      </Container>
    </Section>
  );
};

export default Testimonials;
